import type { Dispatch, SetStateAction } from "react";
import type { UnsavedColumn } from "./types";
import { AccessButton } from "@acdc2/ui/components/access-button";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TableDimensionInput } from "./TableDimensionInput";
import { v4 as uuid } from "uuid";
import { AccessSeparator } from "@acdc2/ui/components/access-separator";
import {
  FormControl,
  FormField,
  FormFieldset,
  FormLegend,
} from "@acdc2/ui/components/access-form";

type Props = {
  columns: UnsavedColumn[];
  setColumns: Dispatch<SetStateAction<UnsavedColumn[]>>;
};

export function TableColumnListFormField({ columns, setColumns }: Props) {
  const { t } = useTranslation();
  const [unsavedColumns, setUnsavedColumns] = useState<UnsavedColumn[]>([]);

  const createUnsavedColumn = () => {
    setUnsavedColumns((oldColumns) => [
      ...oldColumns,
      { uuid: uuid(), width: 0 },
    ]);
  };

  const removeUnsavedColumn = (columnId: string) => {
    setUnsavedColumns((oldColumns) =>
      oldColumns.filter((column) => column.uuid !== columnId),
    );
  };

  const onSave = (index: number, newWidth: number) => {
    setColumns((oldColumns) => {
      const newColumns = [...oldColumns];
      newColumns[index].width = newWidth;
      return newColumns;
    });
  };

  const onDelete = (index: number) => {
    setColumns((oldColumns) => {
      const newColumns = [...oldColumns];
      newColumns.splice(index, 1);
      return newColumns;
    });
  };

  return (
    <FormFieldset>
      <FormLegend>{t("TableColumnListFormField.title")}</FormLegend>
      {columns.map((column, index) => (
        <FormField key={column.uuid} name="tableColumnList">
          <FormControl asChild>
            <TableDimensionInput
              defaultValue={column.width}
              onDelete={() => onDelete(index)}
              onSave={(newWidth: number) => onSave(index, newWidth)}
            />
          </FormControl>
        </FormField>
      ))}

      {unsavedColumns.length > 0 && (
        <AccessSeparator
          className="my-1"
          title={t("MetadataAuthorAccordionItem.separator")}
        />
      )}
      {unsavedColumns.map((column) => (
        <TableDimensionInput
          key={column.uuid}
          defaultValue={column.width}
          onDelete={() => removeUnsavedColumn(column.uuid)}
          onSave={(newWidth: number) => {
            setColumns((oldColumns) => [
              ...oldColumns,
              { ...column, width: newWidth },
            ]);
            removeUnsavedColumn(column.uuid);
          }}
        />
      ))}

      <AccessButton type="button" onClick={() => createUnsavedColumn()}>
        {t("TableColumnListFormField.labels.add")}
      </AccessButton>
    </FormFieldset>
  );
}
